import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Save, X, Edit, Trash2, Search, ChevronLeft, ChevronRight } from "lucide-react";

/** Exactly three digits → prepend one zero (e.g. "275" → "0275"). */
function formatCompanyNumberPadded(v) {
  const s = String(v ?? "").trim();
  if (/^\d{3}$/.test(s)) {
    return `0${s}`;
  }
  return s;
}

const JACKET_ESTIMATE_TYPE_OPTIONS = ["CL EST", "CP"];

/** {@code act_jobstatus} rows from API: {@code jobstatId}, {@code descrip}. */
function normalizeJobStatusOptions(options) {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }
  return options
    .map((opt) => {
      const jobstatId = String(opt?.jobstatId ?? opt?.jobstat_id ?? "").trim();
      if (!jobstatId) {
        return null;
      }
      const descrip = opt?.descrip != null ? String(opt.descrip).trim() : "";
      return { jobstatId, descrip };
    })
    .filter(Boolean);
}

function jacketJobStatusDisplayLabel(codeRaw, options) {
  const code = String(codeRaw ?? "").trim();
  if (!code) {
    return "—";
  }
  const rows = normalizeJobStatusOptions(options);
  const row = rows.find((r) => r.jobstatId === code);
  if (row?.descrip) {
    return row.descrip;
  }
  return code;
}

function jacketJobStatusIsActiveLike(codeRaw, options) {
  const code = String(codeRaw ?? "").trim();
  if (code === "A" || code === "Active") {
    return true;
  }
  const label = jacketJobStatusDisplayLabel(code, options);
  return /^active$/i.test(label);
}

/** Delay before closing notes hover lightbox when pointer leaves (matches Jackets DB table). */
const EXISTING_JACKET_NOTES_LIGHTBOX_CLOSE_MS = 420;

/** Page size for the Existing Jackets table on the Jackets tab. */
const EXISTING_JACKETS_PAGE_SIZE = 15;

/** Stored value is "" | "Y" | "N"; coerces legacy boolean if present. */
function jacketReworkSelectValue(rework) {
  if (rework === true) {
    return "Y";
  }
  if (rework === false || rework == null) {
    return "";
  }
  const s = String(rework).trim();
  if (s === "Y" || s === "N") {
    return s;
  }
  return "";
}

function jacketReworkOrphanValue(rework) {
  const normalized = jacketReworkSelectValue(rework);
  if (normalized !== "") {
    return null;
  }
  if (rework === false || rework == null) {
    return null;
  }
  const s = String(rework).trim();
  return s === "" ? null : s;
}

const JACKET_FIELD_REQUIRED_MSG = "Field Required";

function formatLocalTodayMmDdYyyy() {
  const n = new Date();
  const mm = String(n.getMonth() + 1).padStart(2, "0");
  const dd = String(n.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${n.getFullYear()}`;
}

/** `YYYY-MM-DD` only; returns a user message if invalid or strictly before local today, else null. */
function mustHaveDueDateValidationMessage(raw) {
  const s = String(raw ?? "").trim();
  if (s === "") {
    return null;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) {
    return "Must have date must be YYYY-MM-DD.";
  }
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const entered = new Date(y, mo - 1, d);
  if (
    Number.isNaN(entered.getTime()) ||
    entered.getFullYear() !== y ||
    entered.getMonth() !== mo - 1 ||
    entered.getDate() !== d
  ) {
    return "Must have date must be a valid calendar date.";
  }
  const now = new Date();
  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (entered < todayLocal) {
    return `Must have date cannot be before today (${formatLocalTodayMmDdYyyy()}).`;
  }
  return null;
}

function isJacketValueBlank(v) {
  if (v == null) {
    return true;
  }
  if (typeof v === "boolean") {
    return false;
  }
  return String(v).trim() === "";
}

function computeJacketRequiredFieldErrors(fd) {
  const pairs = [
    ["clientName", fd.clientName],
    ["customerNumber", fd.customerNumber],
    ["jobType", fd.jobType],
    ["group", fd.group],
    ["product", fd.product],
    ["costCenter", fd.costCenter],
    ["accountExecutive", fd.accountExecutive],
    ["billCoordinator", fd.billCoordinator],
    ["csr", fd.csr],
    ["teamManager", fd.teamManager],
    ["estimateNumber", fd.estimateNumber],
    ["companyNumber", fd.companyNumber],
    ["description1", fd.description1],
    ["description2", fd.description2],
  ];
  const errors = {};
  for (const [key, val] of pairs) {
    if (isJacketValueBlank(val)) {
      errors[key] = JACKET_FIELD_REQUIRED_MSG;
    }
  }
  if (isJacketValueBlank(fd.mustHaveDueDate)) {
    errors.mustHaveDueDate = JACKET_FIELD_REQUIRED_MSG;
  } else {
    const dateMsg = mustHaveDueDateValidationMessage(fd.mustHaveDueDate);
    if (dateMsg) {
      errors.mustHaveDueDate = dateMsg;
    }
  }
  return errors;
}

function mapApiErrorsToFieldMessages(errorsObj) {
  const out = {};
  for (const [k, v] of Object.entries(errorsObj)) {
    if (k === "_global") {
      continue;
    }
    out[k] = typeof v === "string" && v.trim() ? v.trim() : JACKET_FIELD_REQUIRED_MSG;
  }
  return out;
}

/** Document order for focusing the first visible jacket validation message after save. */
const JACKET_FIELD_ERROR_FOCUS_ORDER = [
  "clientName",
  "customerNumber",
  "jobNo",
  "jobType",
  "group",
  "mustHaveDueDate",
  "product",
  "costCenter",
  "accountExecutive",
  "billCoordinator",
  "csr",
  "teamManager",
  "poId",
  "estimateNumber",
  "companyNumber",
  "description1",
  "description2",
  "notes",
];

function jacketStickyNotesTextKeyDown(e) {
  if (e.key === "Tab" && !e.shiftKey) {
    e.preventDefault();
  }
}

/** After validation, keep focus inside the control (not the error line) for these fields. */
const JACKET_FOCUS_INPUT_NOT_ERROR_MESSAGE = new Set(["description1", "description2", "notes"]);

function focusFirstJacketValidationMessage(saveBannerText, custIdText, errors) {
  const tryFocus = (id) => {
    const el = document.getElementById(id);
    if (el && typeof el.focus === "function") {
      el.focus({ preventScroll: false });
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      return true;
    }
    return false;
  };
  const tryFocusFieldOrError = (key) => {
    if (JACKET_FOCUS_INPUT_NOT_ERROR_MESSAGE.has(key)) {
      if (tryFocus(`jacket-input-${key}`)) {
        return true;
      }
    }
    return tryFocus(`jacket-field-error-${key}`);
  };
  if (saveBannerText && tryFocus("jacket-save-error-banner")) {
    return;
  }
  if (custIdText && tryFocus("jacket-custid-error-banner")) {
    return;
  }
  for (const key of JACKET_FIELD_ERROR_FOCUS_ORDER) {
    if (errors[key] && tryFocusFieldOrError(key)) {
      return;
    }
  }
  for (const key of Object.keys(errors)) {
    if (JACKET_FIELD_ERROR_FOCUS_ORDER.includes(key) || key === "custId") {
      continue;
    }
    if (errors[key] && tryFocusFieldOrError(key)) {
      return;
    }
  }
}

function JacketRequiredAsterisk() {
  return (
    <span className="text-red-600 dark:text-red-400 font-semibold" aria-hidden="true">
      *
    </span>
  );
}

function JacketInlineFieldError({ message, fieldKey }) {
  if (!message) {
    return null;
  }
  const id = fieldKey ? `jacket-field-error-${fieldKey}` : undefined;
  const isStickyTextFieldError =
    fieldKey != null && JACKET_FOCUS_INPUT_NOT_ERROR_MESSAGE.has(fieldKey);
  return (
    <p
      id={id}
      tabIndex={fieldKey && !isStickyTextFieldError ? -1 : undefined}
      className="mt-1 text-sm font-bold text-red-600 outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 dark:text-red-400 dark:focus-visible:ring-offset-gray-900"
      role="alert"
    >
      {message}
    </p>
  );
}

const JACKET_READ_ONLY_FIELDSET_CLASS =
  "m-0 min-w-0 border-0 p-0 disabled:[&_input]:cursor-not-allowed disabled:[&_input]:bg-gray-100 disabled:[&_select]:cursor-not-allowed disabled:[&_select]:bg-gray-100 disabled:[&_textarea]:cursor-not-allowed disabled:[&_textarea]:bg-gray-100 dark:disabled:[&_input]:bg-gray-800 dark:disabled:[&_select]:bg-gray-800 dark:disabled:[&_textarea]:bg-gray-800";

function JacketForm({
  show,
  formData,
  editing,
  readOnly = false,
  onSave,
  onCancel,
  onChange,
  jobTypeOptions,
  /** act_master_type: { masterType, descrip } — value master_type, label master_type - descrip */
  masterTypeOptions = [],
  /** act_sub_type: { subType, descrip } — value sub_type, label sub_type - descrip */
  subTypeOptions = [],
  /** act_jobstatus: { jobstatId, descrip } — value jobstat_id, label descrip */
  jobStatusOptions = [],
  costCenterOptions,
  productOptions,
  poDropdownOptions = [],
  isLoadingPoDropdown = false,
  isPoDropdownError = false,
  jackets,
  onEdit,
  onDelete,
  employees = [],
  isLoadingEmployees = false,
  isEmployeeLoadError = false,
  accountExecutiveOptions = [],
  isLoadingAccountExecutiveOptions = false,
  isAccountExecutiveOptionsError = false,
  teamManagerOptionsFromCustomers = [],
  billingCoordinatorOptionsFromCustomers = [],
  isLoadingCustgroupCustomerTeamOptions = false,
  isCustgroupCustomerTeamOptionsError = false,
  /** (empId) => label for orphan Account Planner option when value is not yet in the employee list */
  resolveAccountPlannerOrphanLabel = null,
  companyNumberOptions = [],
  isLoadingCompanyNumbers = false,
  isCompanyNumbersLoadError = false
}) {
  const [existingJacketNotesLightbox, setExistingJacketNotesLightbox] = useState(null);
  const existingJacketNotesCloseTimerRef = useRef(null);
  /** Filters the jackets list in the Existing Jackets table by job number (substring, case-insensitive). */
  const [existingJacketsJobNoSearch, setExistingJacketsJobNoSearch] = useState("");
  const [existingJacketsPage, setExistingJacketsPage] = useState(1);
  /** Pending delete: `id` is passed to `onDelete` (job number for API-backed jackets). */
  const [deleteJacketConfirm, setDeleteJacketConfirm] = useState(null);
  const editJacketPanelRef = useRef(null);
  const jacketsFilteredByJobNo = useMemo(() => {
    const arr = Array.isArray(jackets) ? jackets : [];
    const q = existingJacketsJobNoSearch.trim().toLowerCase();
    if (!q) {
      return arr;
    }
    return arr.filter((j) => {
      const jn = String(j.jobNumber ?? j.id ?? "").toLowerCase();
      return jn.includes(q);
    });
  }, [jackets, existingJacketsJobNoSearch]);
  const existingJacketsTotalFiltered = jacketsFilteredByJobNo.length;
  const existingJacketsTotalPages =
    existingJacketsTotalFiltered === 0
      ? 0
      : Math.ceil(existingJacketsTotalFiltered / EXISTING_JACKETS_PAGE_SIZE);
  const existingJacketsPaged = useMemo(() => {
    if (existingJacketsTotalFiltered === 0) {
      return [];
    }
    const start = (existingJacketsPage - 1) * EXISTING_JACKETS_PAGE_SIZE;
    return jacketsFilteredByJobNo.slice(start, start + EXISTING_JACKETS_PAGE_SIZE);
  }, [jacketsFilteredByJobNo, existingJacketsPage, existingJacketsTotalFiltered]);
  const existingJacketsRangeStart =
    existingJacketsTotalFiltered === 0
      ? 0
      : (existingJacketsPage - 1) * EXISTING_JACKETS_PAGE_SIZE + 1;
  const existingJacketsRangeEnd =
    existingJacketsTotalFiltered === 0
      ? 0
      : Math.min(existingJacketsPage * EXISTING_JACKETS_PAGE_SIZE, existingJacketsTotalFiltered);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveErrorBanner, setSaveErrorBanner] = useState("");
  /** Incremented when save validation fails so we auto-focus the first error message once per failure. */
  const [validationFocusGeneration, setValidationFocusGeneration] = useState(0);

  const handleFieldChange = useCallback(
    (field, value) => {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        delete next._global;
        if (field !== "custId") {
          delete next.custId;
        }
        return next;
      });
      setSaveErrorBanner("");
      onChange(field, value);
    },
    [onChange]
  );

  const cancelExistingJacketNotesCloseTimer = useCallback(() => {
    if (existingJacketNotesCloseTimerRef.current != null) {
      window.clearTimeout(existingJacketNotesCloseTimerRef.current);
      existingJacketNotesCloseTimerRef.current = null;
    }
  }, []);

  const scheduleExistingJacketNotesLightboxClose = useCallback(() => {
    cancelExistingJacketNotesCloseTimer();
    existingJacketNotesCloseTimerRef.current = window.setTimeout(() => {
      setExistingJacketNotesLightbox(null);
      existingJacketNotesCloseTimerRef.current = null;
    }, EXISTING_JACKET_NOTES_LIGHTBOX_CLOSE_MS);
  }, [cancelExistingJacketNotesCloseTimer]);

  const closeExistingJacketNotesLightbox = useCallback(() => {
    cancelExistingJacketNotesCloseTimer();
    setExistingJacketNotesLightbox(null);
  }, [cancelExistingJacketNotesCloseTimer]);

  useEffect(() => () => cancelExistingJacketNotesCloseTimer(), [cancelExistingJacketNotesCloseTimer]);

  useEffect(() => {
    if (!existingJacketNotesLightbox) {
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        closeExistingJacketNotesLightbox();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [existingJacketNotesLightbox, closeExistingJacketNotesLightbox]);

  useEffect(() => {
    if (!deleteJacketConfirm) {
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setDeleteJacketConfirm(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [deleteJacketConfirm]);

  useEffect(() => {
    setExistingJacketsPage(1);
  }, [existingJacketsJobNoSearch, jackets.length]);

  useEffect(() => {
    if (existingJacketsTotalPages > 0 && existingJacketsPage > existingJacketsTotalPages) {
      setExistingJacketsPage(existingJacketsTotalPages);
    }
  }, [existingJacketsPage, existingJacketsTotalPages]);

  useEffect(() => {
    if (!show || !editing) {
      return undefined;
    }
    const id = window.requestAnimationFrame(() => {
      editJacketPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [show, editing, formData.jobNumber]);

  useEffect(() => {
    if (!show) {
      return undefined;
    }
    const raw = String(formData.companyNumber ?? "").trim();
    if (raw !== "" && /^\d{3}$/.test(raw)) {
      handleFieldChange("companyNumber", formatCompanyNumberPadded(raw));
    }
    return undefined;
  }, [show, formData.companyNumber, handleFieldChange]);

  useEffect(() => {
    if (!show || isLoadingAccountExecutiveOptions || isAccountExecutiveOptionsError) {
      return undefined;
    }
    const opts = accountExecutiveOptions;
    if (!Array.isArray(opts) || opts.length !== 1) {
      return undefined;
    }
    const onlyId = String(opts[0].empId);
    const current = String(formData.accountExecutive ?? "").trim();
    if (current === "") {
      handleFieldChange("accountExecutive", onlyId);
    }
    return undefined;
  }, [
    show,
    isLoadingAccountExecutiveOptions,
    isAccountExecutiveOptionsError,
    accountExecutiveOptions,
    formData.accountExecutive,
    handleFieldChange,
  ]);

  useEffect(() => {
    if (!show) {
      setFieldErrors({});
      setSaveErrorBanner("");
      setValidationFocusGeneration(0);
    }
  }, [show]);

  useLayoutEffect(() => {
    if (!show || validationFocusGeneration === 0) {
      return undefined;
    }
    focusFirstJacketValidationMessage(saveErrorBanner, fieldErrors.custId, fieldErrors);
    return undefined;
  }, [validationFocusGeneration, show, saveErrorBanner, fieldErrors]);

  const employeeList = Array.isArray(employees) ? employees : [];

  const renderEmployeeSelect = (field, label, loadingPlaceholder, emptyPlaceholder, dropdownOpts) => {
    const options =
      dropdownOpts?.options != null
        ? Array.isArray(dropdownOpts.options)
          ? dropdownOpts.options
          : []
        : employeeList;
    const loading = dropdownOpts?.loading ?? isLoadingEmployees;
    const loadError = Boolean(dropdownOpts?.loadError);
    const loadErrorMessage =
      typeof dropdownOpts?.loadErrorMessage === "string" && dropdownOpts.loadErrorMessage.trim()
        ? dropdownOpts.loadErrorMessage.trim()
        : "Unable to load options right now.";
    const labelRequired = Boolean(dropdownOpts?.labelRequired);
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}
          {labelRequired ? (
            <>
              {" "}
              <JacketRequiredAsterisk />
            </>
          ) : null}
        </label>
        <select
          value={formData[field]}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          disabled={loading}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        >
          <option value="">{loading ? loadingPlaceholder : emptyPlaceholder}</option>
          {options.map((employee) => (
            <option key={employee.empId} value={String(employee.empId)}>
              {employee.displayName}
            </option>
          ))}
          {formData[field] &&
          !options.some((e) => String(e.empId) === String(formData[field])) ? (
            <option value={formData[field]}>
              {(() => {
                const v = String(formData[field] ?? "").trim();
                if (field === "csr" && typeof resolveAccountPlannerOrphanLabel === "function") {
                  const resolved = resolveAccountPlannerOrphanLabel(v);
                  if (typeof resolved === "string" && resolved.trim()) {
                    return resolved.trim();
                  }
                }
                return formData[field];
              })()}
            </option>
          ) : null}
        </select>
        <JacketInlineFieldError message={fieldErrors[field]} fieldKey={field} />
        {loadError ? (
          <p className="mt-2 text-xs text-red-600 dark:text-red-400">{loadErrorMessage}</p>
        ) : null}
      </div>
    );
  };
  const normalizedJobTypeOptions =
    Array.isArray(jobTypeOptions) && jobTypeOptions.length > 0
      ? jobTypeOptions
          .map((opt) => {
            if (typeof opt === "string") {
              const code = opt.trim();
              return code ? { code, description: "" } : null;
            }
            const code = String(
              opt?.code ?? opt?.jobType ?? opt?.job_type ?? opt?.value ?? ""
            ).trim();
            if (!code) {
              return null;
            }
            const description = String(
              opt?.description ?? opt?.jobTypeDescription ?? opt?.label ?? ""
            ).trim();
            return { code, description };
          })
          .filter(Boolean)
      : [];
  const effectiveJobTypeOptions = normalizedJobTypeOptions;
  const normalizedMasterTypeOptions =
    Array.isArray(masterTypeOptions) && masterTypeOptions.length > 0
      ? masterTypeOptions
          .map((opt) => {
            const code = String(opt?.masterType ?? opt?.master_type ?? "").trim();
            if (!code) {
              return null;
            }
            const description = String(opt?.descrip ?? "").trim();
            return { code, description };
          })
          .filter(Boolean)
      : [];
  const effectiveMasterTypeOptions = normalizedMasterTypeOptions;
  const normalizedSubTypeOptions =
    Array.isArray(subTypeOptions) && subTypeOptions.length > 0
      ? subTypeOptions
          .map((opt) => {
            const code = String(opt?.subType ?? opt?.sub_type ?? "").trim();
            if (!code) {
              return null;
            }
            const description = String(opt?.descrip ?? "").trim();
            return { code, description };
          })
          .filter(Boolean)
      : [];
  const effectiveSubTypeOptions = normalizedSubTypeOptions;
  const normalizedJobStatusOptions = normalizeJobStatusOptions(jobStatusOptions);
  const effectiveJobStatusOptions = normalizedJobStatusOptions;
  const normalizedCostCenterOptions =
    Array.isArray(costCenterOptions) && costCenterOptions.length > 0
      ? costCenterOptions
          .map((opt) => {
            const code = String(
              opt?.costCenterCode ?? opt?.cost_center_code ?? opt?.code ?? opt?.value ?? ""
            ).trim();
            if (!code) {
              return null;
            }
            const description = String(opt?.description ?? opt?.label ?? "").trim();
            return { code, description };
          })
          .filter(Boolean)
      : [];
  const normalizedProductOptions =
    Array.isArray(productOptions) && productOptions.length > 0
      ? productOptions
          .map((opt) => {
            const code = String(
              opt?.productCode ?? opt?.product_code ?? opt?.code ?? opt?.value ?? ""
            ).trim();
            if (!code) {
              return null;
            }
            const productGroup = String(opt?.productGroup ?? opt?.product_group ?? "").trim();
            const productName = String(opt?.productName ?? opt?.product_name ?? opt?.label ?? "").trim();
            return { code, productGroup, productName };
          })
          .filter(Boolean)
      : [];
  const normalizedCompanyNumberOptions =
    Array.isArray(companyNumberOptions) && companyNumberOptions.length > 0
      ? [
          ...new Set(
            companyNumberOptions
              .map((v) => formatCompanyNumberPadded(String(v ?? "").trim()))
              .filter(Boolean)
          ),
        ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      : [];
  const companyNumberValue = formatCompanyNumberPadded(formData.companyNumber);
  const normalizedPoDropdownOptions =
    Array.isArray(poDropdownOptions) && poDropdownOptions.length > 0
      ? poDropdownOptions
          .map((row) => {
            const poId = row?.poId ?? row?.po_id;
            if (poId == null || poId === "") {
              return null;
            }
            const clientPo = String(row?.clientPo ?? row?.client_po ?? "").trim();
            if (!clientPo) {
              return null;
            }
            return { poId: String(poId), clientPo };
          })
          .filter(Boolean)
      : [];
  const reworkOrphanValue = jacketReworkOrphanValue(formData.rework);

  const handleSaveClick = useCallback(async () => {
    if (readOnly) {
      return;
    }
    setSaveErrorBanner("");
    const local = computeJacketRequiredFieldErrors(formData);
    if (Object.keys(local).length > 0) {
      setFieldErrors(local);
      setValidationFocusGeneration((g) => g + 1);
      return;
    }
    setFieldErrors({});
    try {
      if (typeof onSave !== "function") {
        return;
      }
      await onSave();
    } catch (err) {
      const data = err?.data;
      if (data?.errors && typeof data.errors === "object") {
        const g = data.errors._global;
        if (typeof g === "string" && g.trim()) {
          setSaveErrorBanner(g.trim());
        }
        const rest = { ...data.errors };
        delete rest._global;
        setFieldErrors(mapApiErrorsToFieldMessages(rest));
      } else if (typeof data?.detail === "string" && data.detail.trim()) {
        setSaveErrorBanner(data.detail.trim());
      } else {
        setSaveErrorBanner("Save failed. Please try again.");
      }
      setValidationFocusGeneration((gen) => gen + 1);
    }
  }, [formData, onSave, readOnly]);

  return <>
      {
    /* Inline Jacket Form - Shown when adding/editing */
  }
      {show && (
        <div
          ref={editJacketPanelRef}
          className="mb-8 scroll-mt-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">
            {readOnly
              ? `View Jacket - ${String(formData.jobNumber ?? "").trim() || "—"}`
              : editing
                ? `Edit Jacket - ${String(formData.jobNumber ?? "").trim() || "—"}`
                : "New Jacket"}
          </h3>
          {saveErrorBanner ? (
            <div
              id="jacket-save-error-banner"
              tabIndex={-1}
              className="mb-4 rounded border border-red-300 bg-red-50 px-4 py-3 text-sm font-bold text-red-700 outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300 dark:focus-visible:ring-offset-gray-900"
              role="alert"
            >
              {saveErrorBanner}
            </div>
          ) : null}
          {fieldErrors.custId ? (
            <div
              id="jacket-custid-error-banner"
              tabIndex={-1}
              className="mb-4 rounded border border-red-300 bg-red-50 px-4 py-3 text-sm font-bold text-red-700 outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300 dark:focus-visible:ring-offset-gray-900"
              role="alert"
            >
              {fieldErrors.custId}
            </div>
          ) : null}

          <fieldset disabled={readOnly} className={JACKET_READ_ONLY_FIELDSET_CLASS}>
          {
    /* 1. Jacket Overview */
  }
          <div className="mb-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-6">
            <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">Jacket Overview</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Client Name <JacketRequiredAsterisk />
                </label>
                <input
    type="text"
    value={formData.clientName}
    readOnly
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
  />
                <JacketInlineFieldError message={fieldErrors.clientName} fieldKey="clientName" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cust # <JacketRequiredAsterisk />
                </label>
                <input
    type="text"
    value={formData.customerNumber}
    readOnly
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
  />
                <JacketInlineFieldError message={fieldErrors.customerNumber} fieldKey="customerNumber" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Job Number
                </label>
                <input
                  type="text"
                  value={formData.jobNumber}
                  readOnly
                  placeholder="Will Populate After Saving"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
                />
                <JacketInlineFieldError message={fieldErrors.jobNo} fieldKey="jobNo" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <select
    value={formData.status}
    onChange={(e) => handleFieldChange("status", e.target.value)}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  >
                  <option value="">Select status</option>
                  {effectiveJobStatusOptions.map((row) => (
                    <option key={row.jobstatId} value={row.jobstatId}>
                      {row.descrip ? row.descrip : row.jobstatId}
                    </option>
                  ))}
                  {formData.status &&
                  !effectiveJobStatusOptions.some((row) => row.jobstatId === formData.status) ? (
                    <option value={formData.status}>{formData.status}</option>
                  ) : null}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Job Type <JacketRequiredAsterisk />
                </label>
                <select
    value={formData.jobType}
    onChange={(e) => handleFieldChange("jobType", e.target.value)}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  >
                  <option value="">Select Job Type</option>
                  {effectiveJobTypeOptions.map((opt) => {
                    const label = opt.description ? `${opt.code} - ${opt.description}` : opt.code;
                    return <option key={opt.code} value={opt.code}>{label}</option>;
                  })}
                  {formData.jobType &&
                  !effectiveJobTypeOptions.some((opt) => opt.code === formData.jobType) ? (
                    <option value={formData.jobType}>{formData.jobType}</option>
                  ) : null}
                </select>
                <JacketInlineFieldError message={fieldErrors.jobType} fieldKey="jobType" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type
                </label>
                <select
    value={formData.type}
    onChange={(e) => handleFieldChange("type", e.target.value)}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  >
                  <option value="">Select Type</option>
                  {effectiveMasterTypeOptions.map((opt) => {
                    const label = opt.description ? `${opt.code} - ${opt.description}` : opt.code;
                    return (
                      <option key={opt.code} value={opt.code}>
                        {label}
                      </option>
                    );
                  })}
                  {formData.type &&
                  !effectiveMasterTypeOptions.some((opt) => opt.code === formData.type) ? (
                    <option value={formData.type}>{formData.type}</option>
                  ) : null}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sub Type
                </label>
                <select
    value={formData.subType}
    onChange={(e) => handleFieldChange("subType", e.target.value)}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  >
                  <option value="">Select Sub Type</option>
                  {effectiveSubTypeOptions.map((opt) => {
                    const label = opt.description ? `${opt.code} - ${opt.description}` : opt.code;
                    return (
                      <option key={opt.code} value={opt.code}>
                        {label}
                      </option>
                    );
                  })}
                  {formData.subType &&
                  !effectiveSubTypeOptions.some((opt) => opt.code === formData.subType) ? (
                    <option value={formData.subType}>{formData.subType}</option>
                  ) : null}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cycle
                </label>
                <select
    value={formData.cycle}
    onChange={(e) => handleFieldChange("cycle", e.target.value)}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  >
                  <option value="">Select Cycle</option>
                  <option value="3">3 - 15th Billing Cycle</option>
                  <option value="4">4 - 20th Billing Cycle</option>
                  <option value="5">5 - 25th Billing Cycle</option>
                  <option value="6">6 - EOM Billing Cycle</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Group <JacketRequiredAsterisk />
                </label>
                <input
                  type="text"
                  value={formData.group}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
                />
                <JacketInlineFieldError message={fieldErrors.group} fieldKey="group" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Program ID
                </label>
                <input
    type="text"
    value={formData.programId}
    onChange={(e) => handleFieldChange("programId", e.target.value)}
    placeholder="Enter program ID"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Re-Work
                </label>
                <select
                  value={jacketReworkSelectValue(formData.rework)}
                  onChange={(e) => handleFieldChange("rework", e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Select Re-Work (optional)</option>
                  <option value="Y">Yes</option>
                  <option value="N">No</option>
                  {reworkOrphanValue ? <option value={reworkOrphanValue}>{reworkOrphanValue}</option> : null}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Must Have Date <JacketRequiredAsterisk />
                </label>
                <input
    type="date"
    value={formData.mustHaveDueDate}
    onChange={(e) => handleFieldChange("mustHaveDueDate", e.target.value)}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
                <JacketInlineFieldError message={fieldErrors.mustHaveDueDate} fieldKey="mustHaveDueDate" />
              </div>
              <div className="col-start-2 flex items-center">
                <label className="flex items-center cursor-pointer">
                  <input
    type="checkbox"
    checked={formData.rush}
    onChange={(e) => handleFieldChange("rush", e.target.checked)}
    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
  />
                  <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">Rush</span>
                </label>
              </div>
            </div>
          </div>

          {
    /* 2. Financial */
  }
          <div className="mb-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-6">
            <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">Financial</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Product <JacketRequiredAsterisk />
                </label>
                <select
    value={formData.product}
    onChange={(e) => handleFieldChange("product", e.target.value)}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  >
                  <option value="">Select Product</option>
                  {normalizedProductOptions.map((product) => {
                    const namePart = product.productName || product.code;
                    const label = product.productGroup
                      ? `${product.productGroup} - ${namePart}`
                      : namePart;
                    return <option key={product.code} value={product.code}>{label}</option>;
                  })}
                  {formData.product &&
                  !normalizedProductOptions.some((product) => product.code === formData.product) ? (
                    <option value={formData.product}>{formData.product}</option>
                  ) : null}
                </select>
                <JacketInlineFieldError message={fieldErrors.product} fieldKey="product" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cost Center <JacketRequiredAsterisk />
                </label>
                <select
    value={formData.costCenter}
    onChange={(e) => handleFieldChange("costCenter", e.target.value)}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  >
                  <option value="">Select Cost Center</option>
                  {normalizedCostCenterOptions.map((center) => {
                    const label = center.description
                      ? `${center.code} - ${center.description}`
                      : center.code;
                    return <option key={center.code} value={center.code}>{label}</option>;
                  })}
                  {formData.costCenter &&
                  !normalizedCostCenterOptions.some((center) => center.code === formData.costCenter) ? (
                    <option value={formData.costCenter}>{formData.costCenter}</option>
                  ) : null}
                </select>
                <JacketInlineFieldError message={fieldErrors.costCenter} fieldKey="costCenter" />
              </div>
              <div className="flex items-center">
                <label className="flex items-center cursor-pointer">
                  <input
    type="checkbox"
    checked={formData.billable}
    onChange={(e) => handleFieldChange("billable", e.target.checked)}
    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
  />
                  <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">Billable</span>
                </label>
              </div>
            </div>
          </div>

          {
    /* 3. Team */
  }
          <div className="mb-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-6">
            <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">Team</h4>
            <div className="grid grid-cols-2 gap-4">
              {renderEmployeeSelect("accountExecutive", "Account Executive", "Loading account executives...", "Select account executive", {
                options: accountExecutiveOptions,
                loading: isLoadingAccountExecutiveOptions,
                loadError: isAccountExecutiveOptionsError,
                loadErrorMessage: "Unable to load account executives from act_custgroup for this customer group.",
                labelRequired: true,
              })}
              {renderEmployeeSelect(
                "billCoordinator",
                "Billing Coordinator",
                "Loading billing coordinators...",
                "Select billing coordinator",
                {
                  options: billingCoordinatorOptionsFromCustomers,
                  loading: isLoadingCustgroupCustomerTeamOptions,
                  labelRequired: true,
                }
              )}
              {renderEmployeeSelect(
                "csr",
                "Account Planner",
                "Loading account planners...",
                "Select account planner",
                { labelRequired: true }
              )}
              {renderEmployeeSelect("teamManager", "Team Manager", "Loading team managers...", "Select team manager", {
                options: teamManagerOptionsFromCustomers,
                loading: isLoadingCustgroupCustomerTeamOptions,
                labelRequired: true,
              })}
            </div>
            {isCustgroupCustomerTeamOptionsError ? (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                Unable to load team manager and billing coordinator options from act_customers for this customer
                group.
              </p>
            ) : null}
            {isEmployeeLoadError ? (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                Unable to load account planner options (employee directory).
              </p>
            ) : null}
          </div>

          {
    /* 4. References */
  }
          <div className="mb-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-6">
            <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">References</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cost Code
                </label>
                <input
    type="text"
    value={formData.costCode}
    onChange={(e) => handleFieldChange("costCode", e.target.value)}
    placeholder="Enter cost code"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  PO Number
                </label>
                <select
                  value={formData.poId}
                  onChange={(e) => handleFieldChange("poId", e.target.value)}
                  disabled={isLoadingPoDropdown}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                >
                  <option value="">
                    {isLoadingPoDropdown ? "Loading PO numbers..." : "Select PO (optional)"}
                  </option>
                  {normalizedPoDropdownOptions.map((row) => (
                    <option key={row.poId} value={row.poId}>
                      {row.clientPo}
                    </option>
                  ))}
                  {formData.poId &&
                  !normalizedPoDropdownOptions.some((row) => row.poId === String(formData.poId).trim()) ? (
                    <option value={String(formData.poId).trim()}>Saved PO (ref: {formData.poId})</option>
                  ) : null}
                </select>
                <JacketInlineFieldError message={fieldErrors.poId} fieldKey="poId" />
                {isPoDropdownError ? (
                  <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                    Unable to load PO numbers from the database.
                  </p>
                ) : null}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estimate Type <JacketRequiredAsterisk />
                </label>
                <select
                  value={formData.estimateNumber}
                  onChange={(e) => handleFieldChange("estimateNumber", e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Select estimate type</option>
                  {JACKET_ESTIMATE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                  {formData.estimateNumber &&
                  !JACKET_ESTIMATE_TYPE_OPTIONS.includes(String(formData.estimateNumber).trim()) ? (
                    <option value={String(formData.estimateNumber).trim()}>
                      {String(formData.estimateNumber).trim()}
                    </option>
                  ) : null}
                </select>
                <JacketInlineFieldError message={fieldErrors.estimateNumber} fieldKey="estimateNumber" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Previous Job
                </label>
                <input
    type="text"
    value={formData.previousJob}
    onChange={(e) => handleFieldChange("previousJob", e.target.value)}
    placeholder="Enter previous job"
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company Number <JacketRequiredAsterisk />
                </label>
                <select
                  value={formData.companyNumber === "" ? "" : companyNumberValue}
                  onChange={(e) => {
                    const v = e.target.value;
                    handleFieldChange("companyNumber", v === "" ? "" : formatCompanyNumberPadded(v));
                  }}
                  disabled={isLoadingCompanyNumbers}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                >
                  <option value="">
                    {isLoadingCompanyNumbers ? "Loading company numbers..." : "Select Company Number"}
                  </option>
                  {normalizedCompanyNumberOptions.map((no) => (
                    <option key={no} value={no}>
                      {no}
                    </option>
                  ))}
                  {companyNumberValue &&
                  !normalizedCompanyNumberOptions.includes(companyNumberValue) ? (
                    <option value={companyNumberValue}>{companyNumberValue}</option>
                  ) : null}
                </select>
                <JacketInlineFieldError message={fieldErrors.companyNumber} fieldKey="companyNumber" />
                {isCompanyNumbersLoadError ? (
                  <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                    Unable to load company numbers from the database.
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          {
    /* 6. Notes */
  }
          <div className="mb-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-6">
            <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">Notes</h4>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description 1 <JacketRequiredAsterisk />
                </label>
                <input
    id="jacket-input-description1"
    type="text"
    value={formData.description1}
    onChange={(e) => handleFieldChange("description1", e.target.value)}
    onKeyDown={jacketStickyNotesTextKeyDown}
    placeholder="Enter description 1"
    aria-invalid={fieldErrors.description1 ? true : undefined}
    aria-describedby={fieldErrors.description1 ? "jacket-field-error-description1" : undefined}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
                <JacketInlineFieldError message={fieldErrors.description1} fieldKey="description1" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description 2 <JacketRequiredAsterisk />
                </label>
                <input
    id="jacket-input-description2"
    type="text"
    value={formData.description2}
    onChange={(e) => handleFieldChange("description2", e.target.value)}
    onKeyDown={jacketStickyNotesTextKeyDown}
    placeholder="Enter description 2"
    aria-invalid={fieldErrors.description2 ? true : undefined}
    aria-describedby={fieldErrors.description2 ? "jacket-field-error-description2" : undefined}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
                <JacketInlineFieldError message={fieldErrors.description2} fieldKey="description2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes
                </label>
                <textarea
    id="jacket-input-notes"
    value={formData.notes}
    onChange={(e) => handleFieldChange("notes", e.target.value)}
    onKeyDown={jacketStickyNotesTextKeyDown}
    placeholder="Enter notes"
    rows={4}
    aria-invalid={fieldErrors.notes ? true : undefined}
    aria-describedby={fieldErrors.notes ? "jacket-field-error-notes" : undefined}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
                <JacketInlineFieldError message={fieldErrors.notes} fieldKey="notes" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Billing Info
                </label>
                <textarea
    value={formData.billingInfo}
    onChange={(e) => handleFieldChange("billingInfo", e.target.value)}
    placeholder="Enter billing information"
    rows={4}
    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
  />
              </div>
            </div>
          </div>
          </fieldset>

          {
    /* Action Buttons */
  }
          <div className="flex items-center gap-4">
            {readOnly ? null : (
            <button
    className="app-btn-gloss px-6"
    type="button"
    onClick={handleSaveClick}
  >
              <Save className="w-4 h-4" />
              {editing ? "Save Changes" : "Create Jacket"}
            </button>
            )}
            <button
    className="app-btn-gloss-cancel px-6"
    onClick={onCancel}
  >
              <X className="w-4 h-4" />
              {readOnly ? "Close" : "Cancel"}
            </button>
          </div>
        </div>
      )}

      {
    /* Existing Jackets List — search is always visible; table body shows empty states when needed */
  }
      <div className="mt-6">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Existing Jackets</h3>
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
              <input
                type="search"
                aria-label="Search existing jackets by job number"
                placeholder="Search by job number…"
                value={existingJacketsJobNoSearch}
                onChange={(e) => setExistingJacketsJobNoSearch(e.target.value)}
                disabled={jackets.length === 0}
                className="w-full rounded border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-400"
              />
            </div>
          </div>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                    Jacket Number
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                    Notes
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 w-[1%] whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {jackets.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      {readOnly
                        ? "No jackets for this customer yet."
                        : "No jackets for this customer yet. Use Add Jacket to create one."}
                    </td>
                  </tr>
                ) : jacketsFilteredByJobNo.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      No jackets match this job number.
                    </td>
                  </tr>
                ) : (
                  existingJacketsPaged.map((jacket) => {
                  const desc1 = String(jacket.description1 ?? jacket.jobdesc1 ?? "").trim();
                  const notesText = jacket.notes != null ? String(jacket.notes).trim() : "";
                  const statusLabel = jacketJobStatusDisplayLabel(jacket.status, jobStatusOptions);
                  const statusActive = jacketJobStatusIsActiveLike(jacket.status, jobStatusOptions);
                  return (
                    <tr key={jacket.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/80">
                      <td className="px-4 py-3 align-top">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          {jacket.jobNumber || "—"}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                          <span
                            className={`inline-flex rounded px-1.5 py-0.5 ${
                              statusActive
                                ? "bg-blue-600 text-white"
                                : "bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-100"
                            }`}
                          >
                            {statusLabel}
                          </span>
                          <span className="text-gray-400 dark:text-gray-500">|</span>
                          <span>
                            {jacket.jobType || "N/A"} · {jacket.customer || jacket.clientName || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top text-sm text-gray-900 dark:text-gray-100 max-w-md">
                        <span className="break-words line-clamp-4">{desc1 !== "" ? desc1 : "—"}</span>
                      </td>
                      <td className="px-4 py-3 align-top text-sm">
                        {notesText !== "" ? (
                          <span
                            className="inline-block"
                            onMouseLeave={scheduleExistingJacketNotesLightboxClose}
                          >
                            <button
                              type="button"
                              className="app-btn-plain font-medium text-blue-600 underline decoration-blue-600/40 underline-offset-2 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                              onMouseEnter={() => {
                                cancelExistingJacketNotesCloseTimer();
                                setExistingJacketNotesLightbox({ text: notesText });
                              }}
                            >
                              View Notes
                            </button>
                          </span>
                        ) : (
                          <span className="text-gray-500 dark:text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                        <div className="inline-flex items-center justify-end gap-1">
                          <button
                            type="button"
                            className="app-btn-plain p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                            onClick={() => onEdit(jacket)}
                            title={readOnly ? "View jacket" : "Edit jacket"}
                          >
                            <Edit className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          </button>
                          {readOnly ? null : (
                          <button
                            type="button"
                            className="app-btn-plain p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                            onClick={() =>
                              setDeleteJacketConfirm({
                                id: jacket.id,
                                jobNumber: String(jacket.jobNumber ?? jacket.id ?? "").trim() || "—",
                              })
                            }
                            title="Delete jacket"
                          >
                            <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
                )}
              </tbody>
            </table>
            </div>
          {existingJacketsTotalFiltered > 0 ? (
            <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50/80 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/50 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Showing{" "}
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {existingJacketsRangeStart}
                </span>
                –
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {existingJacketsRangeEnd}
                </span>{" "}
                of{" "}
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {existingJacketsTotalFiltered}
                </span>
                {existingJacketsTotalFiltered !== (Array.isArray(jackets) ? jackets.length : 0) ? (
                  <span className="text-gray-500 dark:text-gray-500"> (filtered)</span>
                ) : null}
              </p>
              {existingJacketsTotalPages > 1 ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setExistingJacketsPage((p) => Math.max(1, p - 1))}
                    disabled={existingJacketsPage <= 1}
                    className="app-btn-gloss px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </button>
                  <span className="px-2 text-sm tabular-nums text-gray-600 dark:text-gray-400">
                    Page {existingJacketsPage} of {existingJacketsTotalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setExistingJacketsPage((p) =>
                        Math.min(existingJacketsTotalPages, p + 1)
                      )
                    }
                    disabled={existingJacketsPage >= existingJacketsTotalPages}
                    className="app-btn-gloss px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          </div>
        </div>

      {existingJacketNotesLightbox
        ? createPortal(
            <div
              className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-8"
              role="dialog"
              aria-modal="true"
              aria-labelledby="existing-jacket-notes-lightbox-title"
              onMouseEnter={cancelExistingJacketNotesCloseTimer}
              onMouseLeave={scheduleExistingJacketNotesLightboxClose}
            >
              <button
                type="button"
                className="app-btn-plain absolute inset-0 bg-black/45 backdrop-blur-[1px]"
                aria-label="Close notes"
                onClick={closeExistingJacketNotesLightbox}
              />
              <div
                className="relative z-[1] flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-600 dark:bg-gray-900"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">
                  <h2
                    id="existing-jacket-notes-lightbox-title"
                    className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                  >
                    Notes
                  </h2>
                </div>
                <div className="min-h-[80px] flex-1 overflow-y-auto p-5">
                  <p className="whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100">
                    {existingJacketNotesLightbox.text}
                  </p>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {deleteJacketConfirm
        ? createPortal(
            <div
              className="fixed inset-0 z-[210] flex items-center justify-center p-4 sm:p-8"
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-jacket-dialog-title"
              aria-describedby="delete-jacket-dialog-desc"
            >
              <button
                type="button"
                className="app-btn-plain absolute inset-0 bg-black/50 backdrop-blur-[1px]"
                aria-label="Cancel delete"
                onClick={() => setDeleteJacketConfirm(null)}
              />
              <div
                className="relative z-[1] w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-2xl dark:border-gray-600 dark:bg-gray-900"
                onClick={(e) => e.stopPropagation()}
              >
                <h2
                  id="delete-jacket-dialog-title"
                  className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                >
                  Delete jacket?
                </h2>
                <p id="delete-jacket-dialog-desc" className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                  You are about to delete jacket{" "}
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {deleteJacketConfirm.jobNumber}
                  </span>
                  . This action cannot be undone. Are you sure you want to continue?
                </p>
                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
                  <button
                    type="button"
                    className="app-btn-gloss-cancel"
                    onClick={() => setDeleteJacketConfirm(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="app-btn-gloss"
                    onClick={() => {
                      const targetId = deleteJacketConfirm.id;
                      setDeleteJacketConfirm(null);
                      onDelete(targetId);
                    }}
                  >
                    Delete jacket
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>;
}
export {
  JacketForm
};
